import Link from 'next/link'
import { NextSeo } from 'next-seo'
import { Header } from '@components/Header'
import { Footer } from '@components/Footer'
import { ArrowDownIcon, GlobeIcon } from '@heroicons/react/solid'
import { LinkedInSquare } from '@components/UI/Icons/linkedinSquare'
import { FacebookSquare } from '@components/UI/Icons/facebookSquare'
import { RssSquare } from '@components/UI/Icons/rssSquare'
import { GithubIcon } from '@components/UI/Icons/github'

const roles = ['founders', 'developers', 'analysts']
const members = {
  founders: [
    {
      name: 'Lee Eckroth',
      title: 'CEO',
      image: '/images/about/lee-eckroth.png',
      links: [
        {
          href: 'https://www.linkedin.com/in/lee-eckroth-3736bb17/',
          icon: <LinkedInSquare className="mr-1 w-4 h-4 fill-current" />,
          label: 'LinkedIn'
        },
        {
          href: 'https://www.facebook.com/Talk2Lee',
          icon: (
            <FacebookSquare className="mr-1 w-4 h-4 fill-current text-blue-800" />
          ),
          label: 'Facebook'
        }
      ]
    },
    {
      name: 'James Furlo',
      title: 'COO',
      image: '/images/about/james-furlo.png',
      links: [
        {
          href: 'https://www.linkedin.com/in/jamesfurlo/',
          icon: <LinkedInSquare className="mr-1 w-4 h-4 fill-current" />,
          label: 'LinkedIn'
        },
        {
          href: 'https://www.outsidetheratrace.com/',
          icon: (
            <RssSquare className="mr-1 w-4 h-4 fill-current text-orange-500" />
          ),
          label: 'Blog'
        }
      ]
    },
    {
      name: 'Matthew Furlo',
      title: 'Backend Engineer',
      image: '/images/about/matthew-furlo.png',
      links: [
        {
          href: 'https://www.linkedin.com/in/matthew-dean-furlo/',
          icon: <LinkedInSquare className="mr-1 w-4 h-4 fill-current" />,
          label: 'LinkedIn'
        },
        {
          href: 'https://github.com/nextwiggin4',
          icon: <GithubIcon className="mr-1 w-4 h-4 fill-current text-black" />,
          label: 'Github'
        }
      ]
    },
    {
      name: 'Isaac Avery',
      title: 'UI/UX Engineer',
      image: '/images/about/isaac-avery.png',
      links: [
        {
          href: 'https://www.linkedin.com/in/isaacavery/',
          icon: <LinkedInSquare className="mr-1 w-4 h-4 fill-current" />,
          label: 'LinkedIn'
        },
        {
          href: 'https://github.com/isaacavery',
          icon: <GithubIcon className="mr-1 w-4 h-4 fill-current text-black" />,
          label: 'Github'
        }
      ]
    }
  ],
  developers: [
    {
      name: 'Graham Barber',
      title: 'Lead Software Architect',
      image: '/images/about/graham-barber.png',
      links: [
        {
          href: 'https://github.com/puregarlic',
          icon: <GithubIcon className="mr-1 w-4 h-4 fill-current text-black" />,
          label: 'Github'
        },
        {
          href: 'https://graham.now.sh/',
          icon: (
            <GlobeIcon width={16} height={16} className="mr-1 text-black" />
          ),
          label: 'Website'
        }
      ]
    },
    {
      name: 'Zachary Enas',
      title: 'Lead UI/UX Engineer',
      image: '/images/about/zach-enas.png',
      links: []
    },
    {
      name: 'Lance Holmes',
      title: 'UI/UX Designer Consultant',
      image: '/images/about/lance-holmes.png',
      links: [
        {
          href: 'https://www.linkedin.com/in/lanceholmes/',
          icon: <LinkedInSquare className="mr-1 w-4 h-4 fill-current" />,
          label: 'LinkedIn'
        },
        {
          href: 'http://lanceholmes.design/',
          icon: (
            <GlobeIcon width={16} height={16} className="mr-1 text-black" />
          ),
          label: 'Website'
        }
      ]
    },
    {
      name: 'Daniel Ashcraft',
      title: 'Software Consultant',
      image: '/images/about/daniel-ashcraft.png',
      links: [
        {
          href: 'https://www.linkedin.com/in/daashcraft/',
          icon: <LinkedInSquare className="mr-1 w-4 h-4 fill-current" />,
          label: 'LinkedIn'
        },
        {
          href: 'https://github.com/dashcraft',
          icon: <GithubIcon className="mr-1 w-4 h-4 fill-current text-black" />,
          label: 'Github'
        }
      ]
    }
  ],
  analysts: [
    {
      name: 'Scott Hessel',
      title: 'Analyst',
      image: '/images/about/scott-hessel.png',
      links: []
    },
    {
      name: 'Linda Hessel',
      title: 'Analyst',
      image: '/images/about/linda-hessel.png',
      links: []
    }
  ]
}

export default function About() {
  return (
    <>
      <NextSeo
        title="About Majordomo | Get Accurate Repair Estimates"
        description="Majordomo helps users take quick action by enhancing their home inspection reports with accurate repair pricing & prioritizing what needs to be repaired."
        canonical="https://majordomo.com/about-home-repair-pricing-company"
        openGraph={{
          url: 'https://majordomo.com/about-home-repair-pricing-company',
          title: 'About Majordomo | Get Accurate Repair Estimates',
          description:
            'Majordomo helps users take quick action by enhancing their home inspection reports with accurate repair pricing & prioritizing what needs to be repaired.',
          images: [
            {
              url: 'https://majordomo.com/images/about/about-XL.png',
              width: 600,
              height: 825,
              alt: 'Upper floor of a house including some windows'
            }
          ]
        }}
      />
      <Header />

      <main className="pt-8 px-0 container mx-auto">
        <div className="flex lg:flex-row flex-col justify-evenly lg:pb-36 pb-12">
          <div className="flex flex-col items-center lg:w-1/2 md:p-16 p-8">
            <h1 className="md:text-6xl text-4xl font-black tracking-tight mb-12">
              About Majordomo
            </h1>

            <div className="space-y-4 max-w-lg">
              <p>
                We believe homeowners should be empowered to be responsible
                chief stewards –{' '}
                <a
                  href="https://intercom.help/majordomo/about-majordomo/what-is-a-majordomo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-accentBlue hover:underline font-black"
                >
                  majordomos
                </a>{' '}
                – of their home, their most significant investment. And we
                believe we can help.
              </p>

              <p>
                We also believe owning a home is an incredible opportunity and a
                big responsibility. We’re focused on starting your relationship
                with your home on a solid foundation.
              </p>

              <p>
                This foundation starts during the purchase. Typically, you only
                get a short amount of time to look into the condition of a home
                and determine if you’re satisfied with a home's condition, if
                repairs are needed, or if the price needs adjusting.
              </p>

              <p>
                Traditionally, this is challenging because, during that short
                amount of time, you need to understand all the information in an
                inspection report, decide what to do about the information,
                coordinate with contractors to provide quotes for repairs, and
                much more. It can be overwhelming!
              </p>

              <p>
                We created Majordomo in Corvallis, Oregon to help you take quick
                action by enhancing your home inspection. To know what needs to
                be repaired and estimate how much it will cost during{' '}
                <a
                  href="https://intercom.help/majordomo/en/articles/5412854-what-is-the-second-half-of-the-home-inspection"
                  className="text-gold-400 hover:underline font-black"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  the second half of the home inspection
                </a>
                . We do this with the{' '}
                <Link href="/features/domoreport-repair-estimates">
                  <a className="text-gold-400 hover:underline font-black">
                    Domoreport
                  </a>
                </Link>
                .
              </p>

              <p>
                Know what needs to be repaired and how much it'll cost. No
                longer be overwhelmed. We're here to help.
              </p>
            </div>

            <p className="flex items-center justify-end font-black mt-20">
              Learn more about the Majordomo team{' '}
              <ArrowDownIcon
                width={20}
                height={20}
                className="ml-2 text-gold-400"
              />
            </p>
          </div>

          <div className="lg:flex hidden lg:w-1/2 w-full">
            <img
              src="images/about/about-XL.png"
              alt="A Majordomo"
              className="object-cover w-full"
            />
          </div>
        </div>

        <h4 className="text-center md:text-2xl text-lg font-black tracking-tight md:px-0 px-8 lg:pb-12 pb-8 max-w-md mx-auto">
          Who we are
        </h4>

        <div className="2xl:px-40 xl:px-32 md:px-12 px-8 mx-auto lg:pb-36 pb-12">
          {roles.map(role => {
            return (
              <div className="pb-12">
                <div className="border-black border-b mb-12">
                  <h4 className="font-black capitalize">{role}</h4>
                </div>

                <div className="grid xl:grid-cols-3 md:grid-cols-2 grid-cols-1 gap-4">
                  {members[role].map(member => {
                    return (
                      <div className="flex col-span-1">
                        <img
                          src={member.image}
                          className="object-contain folded-l-3"
                        />

                        <div className="p-4 shadow-sm border-gray-100 border border-l-0 bg-gray-50 w-full rounded-r-xl">
                          <h4 className="font-black text-brand text-lg">
                            {member.name}
                          </h4>
                          <p className="text-sm">{member.title}</p>

                          <div className="flex flex-col mt-4 text-sm">
                            {member.links.map(link => {
                              return (
                                <a
                                  href={link.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex w-min items-center text-accentBlue hover:underline"
                                >
                                  {link.icon} {link.label}
                                </a>
                              )
                            })}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>

        <div className="flex flex-col bg-gray-50 lg:mb-36 mb-12 py-16">
          <div>
            <h4 className="max-w-lg m-auto md:text-2xl text-lg text-center font-black tracking-tight mb-2">
              Our Affiliations
            </h4>
            <p className="text-center md:max-w-lg max-w-sm m-auto sm:px-0 px-8">
              Majordomo is proud to be affiliated with InterNACHI and ASHI. Both
              international organizations aspire to improve home inspections,
              the overall home buying & ownership experience
            </p>
          </div>

          <div className="flex md:flex-row flex-col">
            <div className="flex flex-col justify-center md:w-1/2 w-full md:px-16 p-8">
              <img
                src="/images/product-pages/internachi-logo.png"
                className="w-40 self-center"
              />

              <a
                href="https://www.nachi.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h4 className="md:text-2xl text-lg text-accentBlue font-black hover:underline cursor-pointer">
                  InterNACHI
                </h4>
              </a>

              <p>
                The world’s leading association for home inspectors with free
                training, online certification, business development, and more.
              </p>
            </div>

            <div className="flex flex-col justify-center md:w-1/2 w-full md:px-16 p-8">
              <img
                src="/images/product-pages/ashi-logo.png"
                className="w-36 self-center"
              />

              <a
                href="https://www.homeinspector.org/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <h4 className="md:text-2xl text-lg text-accentBlue font-black hover:underline cursor-pointer">
                  ASHI
                </h4>
              </a>

              <p>
                Committed to progressing the home inspection profession and
                providing resources to promote healthy home ownership practices.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
